import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { redirect, useNavigate } from "@tanstack/react-router";
import type { AuthenticatedUser } from "@/client";
import {
  ApiError,
  OpenAPI,
  readUserMeApiV1UsersMeGet,
  sendMagicLinkApiV1LoginSendMagicLinkPost,
  switchClientApiV1LoginSwitchClientClientCodePost,
} from "@/client";
import { OpenAPI as OpenAPIAdmin } from "@/client-admin";

const isLoggedIn = () => {
  return localStorage.getItem("access_token") !== null;
};

const getCurrentUser = async () => {
  try {
    const response = await readUserMeApiV1UsersMeGet();
    return response;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      // Handle token expired
      localStorage.removeItem("access_token");
      OpenAPI.TOKEN = undefined;
      OpenAPIAdmin.TOKEN = undefined;
      throw redirect({ to: "/login" });
    }
    throw error;
  }
};

export const useAuth = () => {
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    data: user,
    isLoading,
    error: getUserError,
  } = useQuery<AuthenticatedUser | null, Error>({
    queryKey: ["currentUser"],
    queryFn: getCurrentUser,
    enabled: isLoggedIn(),
  });

  const sendMagicLinkMutation = useMutation({
    mutationFn: sendMagicLinkApiV1LoginSendMagicLinkPost,
    onError: (error) => {
      setError(error.message);
    },
  });

  const switchClientMutation = useMutation({
    mutationFn: (clientCode: string) => switchClientApiV1LoginSwitchClientClientCodePost({ clientCode }),
    onSuccess: async (data) => {
      if (data.access_token) {
        localStorage.setItem("access_token", data.access_token);
        OpenAPI.TOKEN = data.access_token;
        queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      }
    },
  });

  const logout = () => {
    localStorage.removeItem("access_token");
    OpenAPI.TOKEN = undefined;
    OpenAPIAdmin.TOKEN = undefined;
    navigate({ to: "/login" });
  };

  return {
    sendMagicLinkMutation,
    switchClientMutation,
    logout,
    user,
    isLoading,
    getUserError,
    error,
    resetError: () => setError(null),
  };
};

export { isLoggedIn };
